import { useTranslation } from 'hooks/api/useTranslation';
import { FC, useState } from 'react';
import cls from './subscriptions.module.scss';
import { InputField } from 'shared/ui/inputField/inputField';
import { ICONS } from 'shared/constants/images';
import { Link } from 'react-router-dom';
import { SubscriptionsTable } from './subscriptions-table/subscriptions-table';
import { track } from 'utils/mixpanel';
import { Filter } from 'shared/constants/color-filters';
import { Icon } from 'shared/ui/icon/icon';
import { useSubscriptions } from 'hooks/api/useSubscriptions';
import { StoreModal } from '../store-modal/store-modal';
import { BuySubscriptionModal } from '../buy-subscription-modal/buy-subscription-modal';
import Button from 'shared/ui/button/button';
import { Card } from 'shared/ui/card/card';
import { useCachedUserType } from 'hooks';

interface SubscriptionsProps {
  subscriptionQuantity: number;
  onSubscriptionQuantityChange: (quantity: number) => void;
  onNavigateToWebsites: () => void;
}

export const Subscriptions: FC<SubscriptionsProps> = ({
  subscriptionQuantity,
  onSubscriptionQuantityChange,
  onNavigateToWebsites,
}) => {
  const { t } = useTranslation();
  const { active, count, isLoading, storeUrl } = useSubscriptions();
  const { isDirect } = useCachedUserType();
  const total = count?.total ?? 0;
  const assigned = count?.assigned ?? 0;
  const unassigned = total - assigned;
  const [searchQuery, setSearchQuery] = useState('');
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
  const [isBuySubscriptionModalOpen, setIsBuySubscriptionModalOpen] =
    useState(false);

  const handleBuySubscription = () => {
    track('AIS:LICENSE_MGMT-BUY_SUBSCRIPTION-CLICKED');
  };

  const handleSubWithoutUrl = () => {
    track('AIS:LICENSE_MGMT-BUY_SUBSCRIPTION-CLICKED-NO_URL');
    setIsStoreModalOpen(true);
  };

  const handleBuySubscriptionStdln = () => {
    track('AIS:LICENSE_MGMT-BUY_SUBSCRIPTION-CLICKED');
    setIsBuySubscriptionModalOpen(true);
  };

  return (
    <Card className={cls.main}>
      <div className={cls.title}>
        <div>
          <h2>{t('licenses')}</h2>
          <p>
            {total} {t('total')} • {assigned} {t('assigned')} • {unassigned}{' '}
            {t('notAssigned')}
          </p>
        </div>
        {isDirect ? (
          <Button className={cls.linkBtn} onClick={handleBuySubscriptionStdln}>
            <span>{t('buySubscriptions')}</span>
          </Button>
        ) : storeUrl ? (
          <Link
            className={cls.linkBtn}
            to={storeUrl}
            target='_blank'
            rel='noopener noreferrer'
            onClick={handleBuySubscription}
          >
            <Icon pathToIcon={ICONS.newTab} filter={Filter.white} />
            <span>{t('buySubscriptions')}</span>
          </Link>
        ) : (
          <Button className={cls.linkBtn} onClick={handleSubWithoutUrl}>
            <Icon pathToIcon={ICONS.newTab} filter={Filter.white} />
            <span>{t('buySubscriptions')}</span>
          </Button>
        )}
      </div>
      <div className={cls.header}>
        <InputField
          placeholder={t('search') + '...'}
          icon={ICONS.search}
          className={cls.searchInput}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>
      <SubscriptionsTable
        licenses={active}
        searchQuery={searchQuery}
        isLoading={isLoading}
        onNavigateToWebsites={onNavigateToWebsites}
      />
      {isStoreModalOpen && (
        <StoreModal
          isOpen={isStoreModalOpen}
          onClose={() => setIsStoreModalOpen(false)}
        />
      )}
      {isBuySubscriptionModalOpen && (
        <BuySubscriptionModal
          isOpen={isBuySubscriptionModalOpen}
          onClose={() => setIsBuySubscriptionModalOpen(false)}
          quantity={subscriptionQuantity}
          onQuantityChange={onSubscriptionQuantityChange}
        />
      )}
    </Card>
  );
};
